import { ReactNode } from 'react';

import { twMerge } from 'tailwind-merge';

import { useAnalytics } from '@domain/analytics/AnalyticsContext';
import { trackEvent } from '@domain/analytics/trackEvent';
import { EventMykissShareClicked } from '@generated/events';
import { LikeButton, LikeType } from '../../../LikeButton/LikeButton';
import { ShareButton } from '../../../ShareButton/ShareButton';

interface PostActionsProps {
  children: ReactNode | ReactNode[];
  className?: string;
  isPostLiked: boolean;
  onLikesInfoClick: () => void;
  onLikesButtonClick: () => void;
  postId: string;
  postLikesCount: number;
  postType: LikeType;
  postURL: string;
}

export const PostActions = ({
  children,
  className,
  isPostLiked,
  onLikesInfoClick: handleLikesInfoClick,
  onLikesButtonClick,
  postId,
  postLikesCount,
  postType,
  postURL,
}: PostActionsProps) => {
  const childrenArray = Array.isArray(children) ? children : [children];
  const [detailsLink, moreCommentsLink] = childrenArray;
  const { currentPage } = useAnalytics();
  const parentComponent = postType;

  const handleLikesButtonClick = (): void => {
    trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        type: 'read-more',
        source: 'post-viewer-like',
        page: currentPage,
      },
    });
    onLikesButtonClick();
  };

  const handleShareButtonClick = (
    destination: EventMykissShareClicked['properties']['destination'],
  ): void => {
    trackEvent({
      name: 'mykiss_share_clicked',
      properties: { page: currentPage, destination, source: 'post-viewer-share' },
    });
  };

  return (
    <footer className={twMerge('p-4', className)}>
      <section className="flex items-start justify-between gap-y-3">
        <div className="relative flex items-start">
          <LikeButton
            className="text-abyss!"
            isLiked={isPostLiked}
            key={postId}
            labelPosition="bottom"
            likesInfoClassName="mt-3"
            numberOfLikes={postLikesCount}
            onLikesButtonClick={handleLikesButtonClick}
            onLikesInfoClick={handleLikesInfoClick}
            type={postType}
          />
          <ShareButton
            className="text-abyss absolute left-12"
            parentComponent={parentComponent}
            onClick={() => {
              handleShareButtonClick('share-link');
            }}
            shareData={{ url: postURL }}
          ></ShareButton>
        </div>
        {detailsLink}
      </section>
      {moreCommentsLink && <section>{moreCommentsLink}</section>}
    </footer>
  );
};
